<template>
  <tr v-if="isShow">
    <th :class="[getHeaderClass()]">{{ properties.label }}</th>
    <td :class="[{horizontal: properties.horizontal}, getHighlightClass()]">
      <div v-if="Array.isArray(properties.properties)"
          :class="{'flex':properties.horizontal}">
        <template v-for="(val, index) in properties.properties">
          <BaseCell
              :ref="`cell_${index}`"
              :key="index"
              :class="[val.class, {mt10: needMarginTop(index)}]"
              :properties="val"
              :display-item="displayItem"
              :editable="editable[val.editableId]"
              :site-code="siteCode"
              :is-copied-all="isCopiedAll"
              :is-copied-sku="isCopiedSku"
              :validate-scope="validateScope"
              :style="customCellStyle(val)"
              :highlight-path-list="highlightPathList"
              :highlight-sku-path-list="highlightSkuPathList"
              @input="(event, item, name) => onChange(val, event, item, name)"
              @focus="(event, item, name) => onFocus(val, event, item, name)"
              @blur="(event, item, name, before, after) => onBlur(val, event, item, name, before, after)">
            <span v-if="properties.colsep && index!==properties.properties.length-1"
                slot="colsep">{{ properties.colsep }}</span>
          </BaseCell>
        </template>
      </div>
    </td>
  </tr>
</template>

<style scoped>
.flex {
  display: flex;
}

th.required:after {
  content: " *";
  color: #ff5a5a;
  font-size: 85%;
}
td > .flex > :not(:last-child) {
  margin-right: 5px;
}
.is-highlight {
  background-color: #FFCCCC;
}

</style>

<script>
import _ from 'lodash'

import x from '@/components/mixins/mixin'

import BaseCell from '@/components/molecules/BaseCell'

export default {
  name: 'BaseTableDetailRow',
  components: {
    BaseCell
  },
  inject: ['parentValidator'],
  mixins: [x],
  props: {
    properties: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    displayItem: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    editable: {
      type: Object,
      default: () => {
        return {}
      }
    },
    siteCode: {
      type: String,
      default: null
    },
    validateScope: {
      type: String,
      default: null
    },
    customCellStyle: {
      type: Function,
      default: () => {}
    },
    highlightPathList: {
      type: Object,
      default: () => {
        return {}
      }
    },
    highlightSkuPathList: {
      type: Object,
      default: () => {
        return {}
      }
    },
    isCopiedAll: {
      type: Boolean,
      default: false
    },
    isCopiedSku: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isShow () {
      if (this.properties.isShow != null || this.properties.funcId) {
        return (this.properties.isShow == null || this.executeIfPossible(this.properties.isShow)) &&
        (!this.properties.funcId || this.$isFunctionShown(this.properties.funcId))
      } else {
        // 内容が１つでも表示であれば表示
        return this.properties.properties.some((property) => {
          return (property.isShow == null || this.executeIfPossible(property.isShow)) &&
          (!property.funcId || this.$isFunctionShown(property.funcId))
        })
      }
    }
  },
  created () {
    this.$validator = this.parentValidator
  },
  methods: {
    getPropertyName (obj) {
      return obj.name ? obj.name : obj.path
    },
    /**
     * ハイライト対象の項目の場合にハイライトクラスを設定
     */
    getHighlightClass () {
      // コピー登録フラグ
      const isCopiedAll = this.isCopiedAll
      // 他SKUからコピーフラグ
      const isCopiedSku = this.isCopiedSku
      const highlightPathList = this.highlightPathList
      const highlightSkuPathList = this.highlightSkuPathList
      let isHighlight = false

      if (isCopiedAll && Object.keys(highlightPathList).length) {
        // コピー登録の場合
        isHighlight = this.isHighlightPath(highlightPathList)
      } else if (isCopiedSku && Object.keys(highlightSkuPathList).length) {
        // 他SKUからコピーの場合
        isHighlight = this.isHighlightPath(highlightSkuPathList)
      }
      return `${this.properties.headerClass} ${isHighlight ? 'is-highlight' : ''}`
    },
    /**
     * プロパティのパスがハイライト対象のパスかどうか判定
     */
    isHighlightPath (highlightPathList) {
      let isHighlight = false
      const property = this.properties.properties[0]
      let path = property.path || ''
      // 実行権限のある項目でなければfalseで処理終了
      if (!(typeof path === 'string' && path !== '') ||
        (property.funcId != null && this.$isFunctionDisabled(property.funcId))) {
        return false
      }
      const pathArray = path.split('.')
      if (pathArray.length > 1 && pathArray[0] === 'attributes') {
        // pathがドット区切りかつ分割した最初の要素が「attributes」→attributesなのでeditableIdを返す。
        path = property.editableId || ''
        isHighlight = (highlightPathList['attributePath'] || []).includes(path)
      } else {
        // ドット区切りでない場合はそのままpathを返す。
        isHighlight = (highlightPathList['path'] || []).includes(path)
      }
      return isHighlight
    },
    getHeaderClass () {
      const hasRequired = _.chain(this.properties)
        .get('properties')
        .map(val => {
          if (!_.get(this.editable, val.editableId, false)) {
            return null
          }
          return typeof val.validateRule === 'function'
            ? val.validateRule(val)
            : val.validateRule
        })
        .filter(val => val != null)
        .join('|')
        .split('|')
        .find(val => val === 'required')
        .value() != null

      return `${this.properties.headerClass} ${hasRequired ? 'required' : ''}`
    },
    onChange (props, event, item, name) {
      this.$_mixin_updateValue(event, item, name)
      if (props.change) {
        props.change(event, item, name)
      }
    },
    onFocus (props, event, item, name) {
      if (props.focus) {
        props.focus(event, item, name)
      }
    },
    onBlur (props, event, item, name, before, after) {
      if (props.blur) {
        props.blur(event, item, name, before, after)
      }
    },
    findObject (obj, path) {
      let i = path.indexOf('.')
      if (i < 0) {
        return obj[path]
      }
      return this.findObject(obj[path.substr(0, i)], path.substr(i + 1))
    },
    needMarginTop (index) {
      return !this.properties.horizontal && index > 0
    },
    /**
     * obj が関数の場合、実行した結果を返却する.
     * obj が関数ではない場合、そのまま返却する.
     * @param {*} obj
     * @returns objの実行結果またはobj
     */
    executeIfPossible (obj) {
      return typeof obj === 'function' ? obj() : obj
    },
    focus (path) {
      const args = [].slice.call(arguments)
      args.splice(0, 1)
      const targetIndex = this.properties.properties.findIndex((element) => element.path === path)
      if (targetIndex >= 0) {
        this.$refs[`cell_${targetIndex}`][0].focus(...args)
      }
    }
  }
}
</script>
